import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {hooks} from '../hooks';
import {utils} from '../utils';
import {svg} from '../assets/svg';
import {theme} from '../constants';

import {
  MdAssignment,
  MdPerson,
  MdEventNote,
  MdNotifications,
  MdMoreHoriz,
  MdArchive,
  MdApps,
  MdCalendarToday,
  MdHistory,
  MdTimeline,
  MdPeople,
} from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import {socketService} from '../services';
import {listCache} from '../utils/listCache';

const tabs = [
  {
    id: 1,
    name: 'Task',
    icon: MdAssignment,
  },
  {
    id: 2,
    name: 'Activity',
    icon: MdTimeline,
  },
  {
    id: 3,
    name: 'PersonalData',
    icon: MdPerson,
  },
  {
    id: 4,
    name: 'Contacts',
    icon: MdPeople,
  },
  // {
  //   id: 5,
  //   name: 'LeaveHistory',
  //   icon: MdEventNote,
  // },
  {
    id: 5,
    name: 'Notification',
    icon: MdNotifications,
  }
];

// Cache key yang harus di-clear saat navigasi via tab bar (agar data selalu fresh)
const TAB_CACHE_KEYS: Record<string, string[]> = {
  Task: ['tasks'],
  Notification: ['notifications'],
};

export const BottomTabBar: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { pathname } = useLocation();

  // BottomTabBar di-mount ulang tiap pindah layar (dirender per-screen, bukan layout
  // persisten), jadi state badge selalu mulai dari 0 dan berkedip sampai fetch selesai.
  // Simpan nilai terakhir di sessionStorage sebagai cache instan sambil menunggu fetch.
  const [unreadCount, setUnreadCount] = useState(
    () => parseInt(sessionStorage.getItem('unread_notif_count') || '0', 10)
  );
  const [unreadChatCount, setUnreadChatCount] = useState(
    () => parseInt(sessionStorage.getItem('unread_chat_count') || '0', 10)
  );
  const authSlice = hooks.useAppSelector((state: any) => state.authSlice);

  useEffect(() => { sessionStorage.setItem('unread_notif_count', String(unreadCount)); }, [unreadCount]);
  useEffect(() => { sessionStorage.setItem('unread_chat_count', String(unreadChatCount)); }, [unreadChatCount]);
  
  // Ambil nama screen dari URL saat ini (contoh: '/Task' -> 'Task')
  const currentTabScreen = pathname.split('/')[1] || 'Task';

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (authSlice?.isAuthenticated) {
        try {
          const res = await axiosInstance.get('/notifications');
          const count = Array.isArray(res.data) ? res.data.filter((n: any) => !n.is_read).length : 0;
          setUnreadCount(count);
        } catch (err) {
          console.error('Error fetching unread notifications:', err);
        }
      }
    };
    fetchUnreadCount();
  }, [authSlice?.isAuthenticated, currentTabScreen]); // Refresh setiap kali ganti tab

  useEffect(() => {
    const handler = () => setUnreadCount(0);
    window.addEventListener('notifications-all-read', handler);
    return () => window.removeEventListener('notifications-all-read', handler);
  }, []);

  // WebSocket listener untuk update badge notifikasi secara real-time
  useEffect(() => {
    if (!authSlice?.isAuthenticated) return;

    const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
    socketService.connect(socketUrl, authSlice?.accessToken || undefined);

    const handleNewNotification = (notification: any) => {
      if (notification.user_id == authSlice?.userId) {
        setUnreadCount((prev) => prev + 1);
      }
    };

    socketService.on('new_notification', handleNewNotification);
    return () => {
      socketService.off('new_notification', handleNewNotification);
    };
  }, [authSlice?.isAuthenticated, authSlice?.userId, authSlice?.accessToken]);

  // Fetch jumlah percakapan chat yang belum dibaca — badge di icon Contacts
  useEffect(() => {
    const fetchUnreadChatCount = async () => {
      if (!authSlice?.isAuthenticated) return;
      try {
        const res = await axiosInstance.get('/chat/unread-count');
        setUnreadChatCount(res.data?.count || 0);
      } catch (err) {
        console.error('Error fetching unread chat count:', err);
      }
    };
    fetchUnreadChatCount();

    // Refetch (bukan sekadar increment/decrement manual) supaya selalu akurat —
    // dipicu saat pesan chat baru masuk, atau saat user selesai baca chat (ChatRoom
    // dispatch event ini sendiri setelah markRead sukses).
    window.addEventListener('chat-read', fetchUnreadChatCount);
    if (authSlice?.isAuthenticated) {
      const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
      socketService.connect(socketUrl, authSlice?.accessToken || undefined);
      socketService.on('new_chat_message', fetchUnreadChatCount);
    }
    return () => {
      window.removeEventListener('chat-read', fetchUnreadChatCount);
      socketService.off('new_chat_message', fetchUnreadChatCount);
    };
  }, [authSlice?.isAuthenticated, authSlice?.accessToken, currentTabScreen]);

  return (
    <nav
      style={{
        bottom: 0,
        zIndex: 999,
        width: '100%',
        position: 'fixed',
        maxWidth: '650px',
        paddingBottom: 20,
        backgroundColor: theme.colors.white,
      }}
      className='container'
    >
      <div
        style={{
          position: 'relative',
          ...utils.rowCenterSpcAround(),
          backgroundColor: theme.colors.mainDark,
          borderRadius: 14,
          padding: '4px 0', // Kurangi padding parent agar bisa dipindah ke button
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon as any;
          return (
            <button
              key={tab.id}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '14px 0', // Tambahkan padding atas-bawah pada button
                margin: 0,
                display: 'flex',
                flex: 1, // Buat button membagi lebar seimbang
                justifyContent: 'center', // Posisikan icon di tengah
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                (TAB_CACHE_KEYS[tab.name] || []).forEach(key => listCache.clear(key));
                navigate(`/${tab.name}`);
              }}
            >
              <div style={{ position: 'relative', display: 'flex' }}>
                <Icon
                  size={24}
                  color={
                    currentTabScreen === tab.name
                      ? theme.colors.mainColor
                      : theme.colors.white
                  }
                />
                
                {/* Contoh Badge untuk tab Notification */}
                {tab.name === 'Notification' && unreadCount > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: '#EF4444', // Warna merah untuk badge
                      color: '#FFFFFF',
                      fontSize: 10,
                      fontWeight: 'bold',
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0 4px',
                      boxSizing: 'border-box',
                      border: `1.5px solid ${theme.colors.mainDark}`, // Border agar menyatu dengan background bar
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </div>
                )}

                {/* Badge unread chat untuk tab Contacts (tempat tab "Chats" berada) */}
                {tab.name === 'Contacts' && unreadChatCount > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: '#EF4444',
                      color: '#FFFFFF',
                      fontSize: 10,
                      fontWeight: 'bold',
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0 4px',
                      boxSizing: 'border-box',
                      border: `1.5px solid ${theme.colors.mainDark}`,
                    }}
                  >
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
